/**
 * Runtime configuration contract (ACT-16862).
 *
 * `window.__ENV__` is written by exactly one file — the served `/env.js`
 * (committed no-op stub in `public/env.js`; rendered with real values by the
 * container entrypoint `nginx/docker-entrypoint.d/40-render-env-js.sh`) — and
 * read by exactly one module: this one. No other code may touch
 * `window.__ENV__` or `process.env.REACT_APP_*`.
 *
 * Precedence per key: `window.__ENV__` → baked `process.env.REACT_APP_*` →
 * hardcoded default. A value that is an empty string OR still starts with
 * `${` (an un-substituted envsubst-style placeholder) counts as UNSET at
 * every tier and falls through to the next one.
 *
 * The sole hardcoded default is `BACKEND_ENDPOINT: '/api'` — same-origin on
 * every planned host, which keeps the nginx `/api/*` proxy working with zero
 * configuration. Every other key is deliberately dark when unset.
 */

export const RUNTIME_ENV_KEYS = [
  'BACKEND_ENDPOINT',
  'ENVIRONMENT',
  'LANDING_URL',
  'SENTRY_DSN',
  'FOREX_KEY',
  'AZURE_LOGIN_URL',
  'AZURE_REDIRECT_URI',
  'AZURE_CLIENT_ID',
  'AZURE_API_SCOPE',
  'AZURE_MARS_CLIENT_ID',
  'AZURE_MARS_API_SCOPE',
] as const;

export type RuntimeEnvKey = (typeof RUNTIME_ENV_KEYS)[number];

export type RuntimeEnv = Partial<Readonly<Record<RuntimeEnvKey, string>>>;

declare global {
  interface Window {
    __ENV__?: RuntimeEnv;
  }
}

// Unset = missing, empty, or a placeholder a generator failed to substitute.
const isSet = (value: string | undefined): value is string =>
  value !== undefined && value !== '' && !value.startsWith('${');

// Baked tier: one explicit literal member expression per key. CRA's
// DefinePlugin only documents literal member access — never build the var
// name dynamically (bracket access on a computed 'REACT_APP_' + key string).
// Thunks defer the read to call time so tests can vary `process.env` per case.
const BAKED_READS: Readonly<Record<RuntimeEnvKey, () => string | undefined>> = {
  BACKEND_ENDPOINT: () => process.env.REACT_APP_BACKEND_ENDPOINT,
  ENVIRONMENT: () => process.env.REACT_APP_ENVIRONMENT,
  LANDING_URL: () => process.env.REACT_APP_LANDING_URL,
  SENTRY_DSN: () => process.env.REACT_APP_SENTRY_DSN,
  FOREX_KEY: () => process.env.REACT_APP_FOREX_KEY,
  AZURE_LOGIN_URL: () => process.env.REACT_APP_AZURE_LOGIN_URL,
  AZURE_REDIRECT_URI: () => process.env.REACT_APP_AZURE_REDIRECT_URI,
  AZURE_CLIENT_ID: () => process.env.REACT_APP_AZURE_CLIENT_ID,
  AZURE_API_SCOPE: () => process.env.REACT_APP_AZURE_API_SCOPE,
  AZURE_MARS_CLIENT_ID: () => process.env.REACT_APP_AZURE_MARS_CLIENT_ID,
  AZURE_MARS_API_SCOPE: () => process.env.REACT_APP_AZURE_MARS_API_SCOPE,
};

const DEFAULTS: RuntimeEnv = {
  BACKEND_ENDPOINT: '/api',
};

export const env = (key: RuntimeEnvKey): string | undefined => {
  const runtime = window.__ENV__?.[key];
  if (isSet(runtime)) {
    return runtime;
  }

  const baked = BAKED_READS[key]();
  if (isSet(baked)) {
    return baked;
  }

  return DEFAULTS[key];
};
